<template>
  <BaseBanner
    v-if="display"
    type="no-line-break-secondary"
    show-icon
    show-close-button
    class="loyalty-cash-banner"
    @close="onClose">
    <template #icon>
      <IconOrigamiDress
        width="32"
        height="32"/>
    </template>
    <template #title>
      <span v-if="partOfMonth === 'mid'">Reminder!</span>
      <span v-else-if="partOfMonth === 'end'">Almost gone!</span>
      <span v-else>{{ month }} Loyalty Cash is live!</span>
    </template>
    <template #text>
      <span v-if="partOfMonth === 'mid'">
        Spend your {{ amountOff }} Loyalty Cash on ANY ITEM from the Big Closet before {{ expireDate }} with <b>{{ promoCode }}</b>.
      </span>
      <span v-else-if="partOfMonth === 'end'">
        Use <b>{{ promoCode }}</b> to apply your {{ amountOff }} Loyalty Cash to ANY ITEM from the Big Closet. Ends {{ expireDate }}.
      </span>
      <span v-else>
        Use <b>{{ promoCode }}</b> to spend your {{ amountOff }} Loyalty Cash on the purchase of ANY ITEM from the Big Closet. Expires {{ expireDate }}.
      </span>
    </template>
  </BaseBanner>
</template>

<script setup>
import BaseBanner from '@/components/headers/BaseBanner.vue'
import { storeToRefs } from 'pinia'
import { useClosetStore } from '@shared/stores/closet.js'
import { computed, ref } from 'vue'
import { useNow } from '@vueuse/core'
import { formatPrice, toDayMonthString } from '@/utils/stringParsing.js'
import IconOrigamiDress from '@shared/components/icons/IconOrigamiDress.vue'
import { useUnlimitedTrialStore } from '@/stores/unlimitedTrial.js'

const { loyaltyCashPromotion } = storeToRefs(useClosetStore())
const { unlimitedTrialActive } = storeToRefs(useUnlimitedTrialStore())

const promoCode = computed(() => loyaltyCashPromotion.value?.promoCode?.toUpperCase() || null)
const amountOff = computed(() =>
  formatPrice(loyaltyCashPromotion.value?.amountOff, true, false, true) ||
  (loyaltyCashPromotion.value?.percentOff ? `${loyaltyCashPromotion.value.percentOff}% off` : null)
)
const start = computed(() => loyaltyCashPromotion.value?.start ? new Date(loyaltyCashPromotion.value.start) : null)
const expiration = computed(() => loyaltyCashPromotion.value?.expiration ? new Date(loyaltyCashPromotion.value.expiration) : null)

const now = useNow({ interval: 60 * 1000 })
const midMonth = computed(() => start.value ? new Date(start.value.getTime() + 13 * 24 * 60 * 60 * 1000) : null) // 14 days after start
const monthEnd = computed(() => expiration.value ? new Date(expiration.value.getTime() - 7 * 24 * 60 * 60 * 1000) : null) // 7 days left
const partOfMonth = computed(() =>
  expiration.value && now.value >= monthEnd.value
    ? 'end'
    : midMonth.value && now.value >= midMonth.value
      ? 'mid'
      : 'start'
)

const month = computed(() => start.value?.toLocaleString('en-US', { month: 'long', timeZone: 'America/Los_Angeles' }))
const expireDate = computed(() => toDayMonthString(expiration.value, 'America/Los_Angeles'))


const started = computed(() => start.value ? now.value >= start.value : false)
const expired = computed(() => expiration.value ? now.value > expiration.value : false)
const dismissed = ref(false)
const display = computed(() =>
  !unlimitedTrialActive.value &&
  promoCode.value &&
  amountOff.value &&
  started.value &&
  !expired.value &&
  !dismissed.value
)

const onClose = () => {
  dismissed.value = true
}
</script>

<style lang="scss" scoped>
.loyalty-cash-banner {
  padding: $spacing-xs $spacing-sm !important;
}
</style>
